import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { useUsersApi } from '../services/usersApi'

interface WhosComingProps {
  rsvps: {
    userId: string
    firstName?: string
    lastName?: string
    userName?: string
    joinedYear?: string
    status: 'yes' | 'no' | 'maybe'
    timestamp?: string
    days?: string[]
  }[]
  simplified?: boolean
}

const WhosComing: React.FC<WhosComingProps> = ({ rsvps, simplified = false }) => {
  const attendingRsvps = useMemo(() => rsvps.filter(rsvp => rsvp.status === 'yes'), [rsvps])
  const usersApi = useUsersApi()
  const [nameMap, setNameMap] = useState<Record<string, string>>({})
  const loadingIdsRef = useRef<Set<string>>(new Set())

  const missingIds = useMemo(() => {
    const ids = attendingRsvps
      .filter(r => !((r.userName && r.userName.trim().length > 0) || ((r.firstName || '').trim() || (r.lastName || '').trim())))
      .map(r => r.userId)
      .filter(id => !nameMap[id])
    return Array.from(new Set(ids)).sort()
  }, [attendingRsvps, nameMap])

  const loadMissingNames = useCallback(async () => {
      try {
        if (missingIds.length === 0) return

        const idsToFetch = missingIds.filter(id => !loadingIdsRef.current.has(id))
        if (idsToFetch.length === 0) return

        idsToFetch.forEach(id => loadingIdsRef.current.add(id))

        const entries = await Promise.all(idsToFetch.map(async (id) => {
          try {
            const user = await usersApi.getUser(id)
            const fullName = (user.name || `${user.firstName || ''} ${user.lastName || ''}`).trim()
            return [id, fullName] as const
          } catch (error) {
            return [id, ''] as const
          }
        }))

        setNameMap(prev => {
          const next = { ...prev }
          for (const [id, name] of entries) {
            if (name) {
              next[id] = name
            }
          }
          return next
        })
        idsToFetch.forEach(id => {
          loadingIdsRef.current.delete(id)
        })
      } catch (error) {
        // Clean up loading state on error
        idsToFetch.forEach(id => loadingIdsRef.current.delete(id))
      }
  }, [missingIds, usersApi, setNameMap])

  useEffect(() => {
    loadMissingNames()
  }, [loadMissingNames])

  if (simplified) {
    return (
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Who's Coming ({attendingRsvps.length})
        </h3>
        
        <div className="space-y-3">
          {attendingRsvps.map((rsvp) => (
            <div key={rsvp.userId} className="flex items-center space-x-3">
              <CheckCircleIcon className="w-5 h-5 text-green-500" />
              <div className="flex items-center space-x-2">
                <span className="text-gray-900 dark:text-white font-medium">
                  {(() => {
                    const mappedName = nameMap[rsvp.userId]
                    const userName = rsvp.userName
                    const fullName = `${rsvp.firstName || ''} ${rsvp.lastName || ''}`.trim()
                    const isLoading = loadingIdsRef.current.has(rsvp.userId)
                    
                    return mappedName || userName || fullName || (isLoading ? 'Loading...' : `User ${rsvp.userId.slice(-4)}`)
                  })()}
                </span>
                {rsvp.joinedYear && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Member since {rsvp.joinedYear}
                  </span>
                )}
                <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  Attending
                </span>
              </div>
            </div>
          ))}
          
          {attendingRsvps.length === 0 && (
            <div className="text-gray-500 dark:text-gray-400 text-center py-4">
              No RSVPs yet
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
        Who's Coming ({attendingRsvps.length})
      </h3>
      
      <div className="space-y-3">
        {attendingRsvps.map((rsvp) => (
          <div key={rsvp.userId} className="flex items-center space-x-3">
            <CheckCircleIcon className="w-5 h-5 text-green-500" />
            <div className="flex flex-col">
              <span className="text-gray-900 dark:text-white font-medium">
                {(() => {
                  const mappedName = nameMap[rsvp.userId]
                  const userName = rsvp.userName
                  const fullName = `${rsvp.firstName || ''} ${rsvp.lastName || ''}`.trim()
                  
                  const isLoading = loadingIdsRef.current.has(rsvp.userId)
                  return mappedName || userName || fullName || (isLoading ? 'Loading...' : `User ${rsvp.userId.slice(-4)}`)
                })()}
              </span>
              {rsvp.joinedYear && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Member since {rsvp.joinedYear}
                </span>
              )}
            </div>
            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
              Attending
            </span>
          </div>
        ))}
        
        {attendingRsvps.length === 0 && (
          <div className="text-gray-500 dark:text-gray-400 text-center py-4">
            No RSVPs yet
          </div>
        )}
      </div>
    </div>
  )
}

export default WhosComing